import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import LabelIcon from "@mui/icons-material/Label";
import LayersIcon from "@mui/icons-material/Layers";
import LinearScaleIcon from "@mui/icons-material/LinearScale";
import MarginIcon from "@mui/icons-material/Margin";
import CompassIcon from "@mui/icons-material/Navigation";
import NearMeIcon from "@mui/icons-material/NearMe";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RouteIcon from "@mui/icons-material/Route";
import StraightenIcon from "@mui/icons-material/Straighten";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import TextureIcon from "@mui/icons-material/Texture";
import TitleIcon from "@mui/icons-material/Title";
import {
  Box,
  Button,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useHangarsState } from "../../../../../containers/HangarsStateContainer";
import { useMeasuringToolState } from "../../../../../containers/MeasuringToolContainer";
import { CalibrationPoint } from "../../../../../types";
import { uuidv4 } from "../../../../../utils";
import { RampIcon } from "../../../../../widgets/Icons/RampIcon";
import { SplitButton } from "../../../../../widgets/SplitButton";
import { CalibrationPointDialog } from "../CalibrationPointDialog";
import { CalibrationPointsList } from "../CalibrationPointsList";
import { GISImportDialog } from "../GISImport/GISImportDialog";
import { EgressCalculationDialog } from "../RampCanvas/EgressCalculationDialog";
import {
  getDefaultSizeCategory,
  Shape,
  SIZE_CATEGORY_OPTIONS,
} from "../RampCanvas/RampCanvas";
import {
  calculateBlockedSpots,
  calculateSpotNames,
  EgressCalculationResult,
} from "../RampCanvas/spotHelpers";
import {
  useCalibrationState,
  useLocalRampState,
  useRampEditorState,
} from "../RampEditorPresenter";
import { ShapeElements } from "./ShapeElements";

export interface Tool {
  name: string;
  icon: JSX.Element;
  shortcut?: string;
  onClick: () => void;
}

type Props = {
  selectedTool: string;
  setSelectedTool: (tool: string) => void;
};

export const Toolbar: React.FC<Props> = ({ selectedTool, setSelectedTool }) => {
  const {
    showReferenceImage,
    setShowReferenceImage,
    showMeasurements,
    setShowMeasurements,
    showGuideLines,
    setShowGuideLines,
    showCalibrationPoints,
    setShowCalibrationPoints,
    highlightedCalibrationPoint,
    setHighlightedCalibrationPoint,
  } = useRampEditorState();
  const { ramp, setRamp, setActiveShapes } = useLocalRampState();
  const { isAddingCalibrationPoint, setIsAddingCalibrationPoint } =
    useCalibrationState();
  const { hangars } = useHangarsState();
  const measuringToolState = useMeasuringToolState();
  const [activeTab, setActiveTab] = React.useState<string>("Tools");
  const [calibrationDialog, setCalibrationDialog] = React.useState<{
    open: boolean;
    x?: number;
    y?: number;
    point?: CalibrationPoint;
  }>({ open: false });
  const [gisImportDialogOpen, setGisImportDialogOpen] =
    React.useState<boolean>(false);
  const [egressDialogOpen, setEgressDialogOpen] =
    React.useState<boolean>(false);
  const [egressResult, setEgressResult] =
    React.useState<EgressCalculationResult | null>(null);

  const [selectedSpotSize, setSelectedSpotSize] = React.useState<number>(100);

  // Options panel collapse state with localStorage persistence
  const [optionsExpanded, setOptionsExpanded] = React.useState<boolean>(() => {
    const saved = localStorage.getItem("ramp-editor-options-expanded");
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Calibration Points panel collapse state with localStorage persistence
  const [calibrationPointsExpanded, setCalibrationPointsExpanded] =
    React.useState<boolean>(() => {
      const saved = localStorage.getItem(
        "ramp-editor-calibration-points-expanded"
      );
      return saved !== null ? JSON.parse(saved) : true;
    });

  // Save options expanded state to localStorage
  React.useEffect(() => {
    localStorage.setItem(
      "ramp-editor-options-expanded",
      JSON.stringify(optionsExpanded)
    );
  }, [optionsExpanded]);

  // Save calibration points expanded state to localStorage
  React.useEffect(() => {
    localStorage.setItem(
      "ramp-editor-calibration-points-expanded",
      JSON.stringify(calibrationPointsExpanded)
    );
  }, [calibrationPointsExpanded]);

  const setShapes = React.useCallback(
    (shapesOrUpdater: Shape[] | ((prevShapes: Shape[]) => Shape[])) => {
      setRamp((currentRamp) => {
        if (!currentRamp) return currentRamp;

        return {
          ...currentRamp,
          shapes:
            typeof shapesOrUpdater === "function"
              ? shapesOrUpdater(currentRamp.shapes || [])
              : shapesOrUpdater,
        };
      });
    },
    [setRamp]
  );

  const handleAddCalibrationPoint = (x: number, y: number) => {
    setCalibrationDialog({ open: true, x, y });
  };

  const handleSaveCalibrationPoint = (point: CalibrationPoint) => {
    const existingIndex = ramp.calibration_points?.findIndex(
      (p) => p.id === point.id
    );
    let updatedPoints = ramp.calibration_points || [];

    if (existingIndex !== undefined && existingIndex >= 0) {
      updatedPoints[existingIndex] = point;
    } else {
      updatedPoints = [...updatedPoints, point];
    }

    setRamp({
      ...ramp,
      calibration_points: updatedPoints,
    });
  };

  const handleDeleteCalibrationPoint = (pointId: string) => {
    setRamp({
      ...ramp,
      calibration_points:
        ramp.calibration_points?.filter((p) => p.id !== pointId) || [],
    });
  };

  const handleImportGISShapes = (newShapes: Shape[]) => {
    setRamp((currentRamp) => ({
      ...currentRamp,
      shapes: [...currentRamp.shapes, ...newShapes],
    }));
    setGisImportDialogOpen(false);
  };

  // Guard to ensure ramp is properly loaded
  const isRampValid = () => {
    if (!ramp || !ramp.id || !ramp.fbo_id) {
      console.error("Ramp not properly loaded - cannot create shape");
      return false;
    }
    return true;
  };

  // Expose the add calibration point function globally for use by canvas
  React.useEffect(() => {
    if (isAddingCalibrationPoint) {
      (window as any).addCalibrationPoint = handleAddCalibrationPoint;
    } else {
      delete (window as any).addCalibrationPoint;
    }
  }, [isAddingCalibrationPoint]);

  const setShape = React.useCallback(
    (idx: number, shape: Shape) => {
      setRamp((currentRamp) => ({
        ...currentRamp,
        shapes: currentRamp.shapes.map((s, i) => (i === idx ? shape : s)),
      }));
    },
    [setRamp]
  );

  const startingPoint = {
    x: (ramp?.width ?? 0) / 2,
    y: (ramp?.depth ?? 0) / 2,
  };

  const DEFAULT_SPOT_COLOR = "#FFFFFF";

  const createSpotShape = (size: number) => {
    if (!isRampValid()) return;
    setActiveShapes([]);

    // Get label and size category for this spot
    const sizeOption = SIZE_CATEGORY_OPTIONS.find(
      (opt) => opt.maxFeet === size
    );
    const sizeCategory = getDefaultSizeCategory(size);

    setShapes([
      {
        id: uuidv4(),
        fbo_id: ramp.fbo_id,
        ramp_id: ramp.id,
        tags: ["spot"],
        type: "Spot",
        locked: false,
        points: [
          { x: startingPoint.x - size / 2, y: startingPoint.y - size / 2 },
          { x: startingPoint.x + size / 2, y: startingPoint.y - size / 2 },
          { x: startingPoint.x + size / 2, y: startingPoint.y + size / 2 },
          { x: startingPoint.x - size / 2, y: startingPoint.y + size / 2 },
        ],
        stroke_style: DEFAULT_SPOT_COLOR,
        line_style: "dashed",
        line_width: 3,
        fill_style: null,
        font_size: 14,
        reference_image_height: null,
        text: sizeOption?.label || size.toString(),
        reference_points: [
          {
            name: "north",
            validOrientations: ["nose_to_line", "tail_to_line"],
          },
        ],
        metadata: {
          allowedSizes: [sizeCategory],
          egressLanes: [],
          blocksSpotIds: [],
        },
      },
      ...ramp.shapes,
    ]);
    setSelectedTool("Select");
    measuringToolState.setActive(false);
  };

  const tools: Tool[] = [
    {
      name: "Select",
      icon: <NearMeIcon />,
      onClick: () => {
        setActiveShapes([]);
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Area",
      icon: <TextureIcon />,
      shortcut: "A",
      onClick: () => {
        setActiveShapes([]);
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Zone",
      icon: <LayersIcon />,
      onClick: () => {
        setActiveShapes([]);
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Line",
      icon: <LinearScaleIcon />,
      shortcut: "L",
      onClick: () => {
        setActiveShapes([]);
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Tie Down T",
      icon: <TitleIcon />,
      shortcut: "Tie Down T",
      onClick: () => {
        if (!isRampValid()) return;
        setActiveShapes([]);
        setShapes([
          {
            id: uuidv4(),
            fbo_id: ramp.fbo_id,
            ramp_id: ramp.id,
            tags: [],
            type: "Tie Down T",
            locked: false,
            points: [
              { x: 0 + startingPoint.x - 50, y: startingPoint.y + 0 },
              { x: startingPoint.x + 100 - 50, y: startingPoint.y + 0 },
              { x: startingPoint.x + 100 - 50, y: startingPoint.y + 100 },
              { x: 0 + startingPoint.x - 50, y: startingPoint.y + 100 },
            ],
            stroke_style: "black",
          },
          ...ramp.shapes,
        ]);
        setSelectedTool("Select");
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Double T",
      icon: (
        <Box
          sx={{
            position: "relative",

            width: 20, // Adjust size as needed
            height: 20,
          }}
        >
          {/* Right-side-up TitleIcon */}
          <TitleIcon
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              fontSize: 20,
              color: "black",
            }}
          />
          {/* Upside-down TitleIcon */}
          <TitleIcon
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              fontSize: 20,
              color: "black", // Different color for distinction
              transform: "rotate(180deg)",
            }}
          />
        </Box>
      ),
      shortcut: "Double T",
      onClick: () => {
        if (!isRampValid()) return;
        setActiveShapes([]);
        setShapes([
          {
            id: uuidv4(),
            fbo_id: ramp.fbo_id,
            ramp_id: ramp.id,
            tags: [],
            type: "Double T",
            locked: false,
            points: [
              { x: 0 + startingPoint.x - 50, y: startingPoint.y + 0 },
              { x: startingPoint.x + 100 - 50, y: startingPoint.y + 0 },
              { x: startingPoint.x + 100 - 50, y: startingPoint.y + 100 },
              { x: 0 + startingPoint.x - 50, y: startingPoint.y + 100 },
            ],
            stroke_style: "black",
          },
          ...ramp.shapes,
        ]);
        setSelectedTool("Select");
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Circle",
      icon: <RadioButtonUncheckedIcon />,
      onClick: () => {
        if (!isRampValid()) return;
        setActiveShapes([]);
        setShapes([
          {
            id: uuidv4(),
            fbo_id: ramp.fbo_id,
            ramp_id: ramp.id,
            tags: [],
            type: "Circle",
            locked: false,
            points: [
              { x: startingPoint.x + 0 - 50, y: startingPoint.y + 0 },
              { x: startingPoint.x + 100 - 50, y: startingPoint.y + 0 },
              { x: startingPoint.x + 100 - 50, y: startingPoint.y + 100 },
              { x: startingPoint.x + 0 - 50, y: startingPoint.y + 100 },
            ],
            stroke_style: "black",
            line_style: "solid",
            line_width: 3,
            fill_style: null,
            font_size: null,
            reference_image_height: null,
            text: null,
          },
          ...ramp.shapes,
        ]);
        setSelectedTool("Select");
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Helipad",
      icon: (
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 24, // Adjust size as needed
            height: 24,
          }}
        >
          <RadioButtonUncheckedIcon />
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              color: "black",
              fontWeight: "bold",
            }}
          >
            H
          </Typography>
        </Box>
      ),
      onClick: () => {
        if (!isRampValid()) return;
        setActiveShapes([]);
        setShapes([
          {
            id: uuidv4(),
            fbo_id: ramp.fbo_id,
            ramp_id: ramp.id,
            tags: [],
            type: "Helipad",
            locked: false,
            points: [
              { x: startingPoint.x + 0 - 50, y: startingPoint.y + 0 },
              { x: startingPoint.x + 100 - 50, y: startingPoint.y + 0 },
              { x: startingPoint.x + 100 - 50, y: startingPoint.y + 100 },
              { x: startingPoint.x + 0 - 50, y: startingPoint.y + 100 },
            ],
            stroke_style: "#f9de55",
            line_style: "solid",
            line_width: 3,
            fill_style: null,
            font_size: 50,
            reference_image_height: null,
            text: "H",
          },
          ...ramp.shapes,
        ]);
        setSelectedTool("Select");
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Spot",
      icon: <MarginIcon />,
      onClick: () => {
        // This is a placeholder - actual rendering uses SplitButton
      },
    },
    {
      name: "Text",
      icon: <TextFieldsIcon />,
      onClick: () => {
        setActiveShapes([]);
        console.log("Text");
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Measuring",
      icon: <StraightenIcon />,
      onClick: () => {
        setActiveShapes([]);
        measuringToolState.setActive(true);
      },
    },
    {
      name: "Compass Rose",
      icon: <CompassIcon />,
      onClick: () => {
        if (!isRampValid()) return;
        setActiveShapes([]);
        // if there is already a compass rose, don't add another one
        if (ramp.shapes.find((s) => s.type === "Compass Rose")) {
          console.log("Already a compass rose in this ramp");
          return;
        }
        setShapes([
          {
            id: uuidv4(),
            fbo_id: ramp.fbo_id,
            ramp_id: ramp.id,
            tags: [],
            type: "Compass Rose",
            locked: false,
            points: [
              { x: 0 + startingPoint.x - 50, y: startingPoint.y + 0 },
              { x: startingPoint.x + 100 - 50, y: startingPoint.y + 0 },
              { x: startingPoint.x + 100 - 50, y: startingPoint.y + 100 },
              { x: 0 + startingPoint.x - 50, y: startingPoint.y + 100 },
            ],
            stroke_style: "#000000",
          },
          ...ramp.shapes,
        ]);
        setSelectedTool("Select");
        measuringToolState.setActive(false);
      },
    },
  ];

  const bulkTools: Tool[] = [
    {
      name: "Import GIS Shapes",
      icon: <RampIcon />,
      onClick: () => {
        setGisImportDialogOpen(true);
        setActiveShapes([]);
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Calculate Egress",
      icon: <RouteIcon />,
      onClick: () => {
        if (!isRampValid()) return;
        const result = calculateBlockedSpots(ramp.shapes);
        setEgressResult(result);
        setShapes(result.shapes);
        setEgressDialogOpen(true);
        setActiveShapes([]);
        measuringToolState.setActive(false);
      },
    },
    {
      name: "Auto-Name Spots",
      icon: <LabelIcon />,
      onClick: () => {
        if (!isRampValid()) return;
        const updatedShapes = calculateSpotNames(ramp.shapes);
        setShapes(updatedShapes);
        setActiveShapes([]);
        measuringToolState.setActive(false);
      },
    },
  ];

  return (
    <Stack direction="column" alignItems="flex-start" spacing={1}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          "&:hover": { backgroundColor: "action.hover" },
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
        }}
        onClick={() => setOptionsExpanded(!optionsExpanded)}
      >
        <Typography variant="subtitle2">Options</Typography>
        {optionsExpanded ? (
          <ExpandLessIcon fontSize="small" />
        ) : (
          <ExpandMoreIcon fontSize="small" />
        )}
      </Box>

      <Collapse in={optionsExpanded} sx={{ width: "100%" }}>
        <Stack direction="column" alignItems="flex-start" spacing={1}>
          <Stack direction="row" spacing={1}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  color="primary"
                  checked={showReferenceImage}
                  onChange={() => {
                    setShowReferenceImage(!showReferenceImage);
                  }}
                />
              }
              label="Image"
              labelPlacement="start"
            />
            <IconButton
              size="small"
              onClick={() => {
                // download the image (ramp.reference_image)
                const a = document.createElement("a");
                a.href = ramp.reference_image;
                a.download = `image-${ramp.name}.png`;
                a.click();
              }}
              sx={{ p: 0 }}
            >
              <DownloadIcon />
            </IconButton>
          </Stack>
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="primary"
                checked={showMeasurements}
                onChange={() => setShowMeasurements(!showMeasurements)}
              />
            }
            label="Measurements"
            labelPlacement="start"
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="primary"
                checked={ramp?.version === "v2"}
                onChange={() =>
                  setRamp({
                    ...ramp,
                    version: ramp?.version === "v2" ? "v1" : "v2",
                  })
                }
              />
            }
            label="Ramp v2.0"
            labelPlacement="start"
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="primary"
                checked={ramp?.autoparking_enabled ?? false}
                onChange={() =>
                  setRamp({
                    ...ramp,
                    autoparking_enabled: !ramp?.autoparking_enabled,
                  })
                }
              />
            }
            label="AutoParking"
            labelPlacement="start"
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="primary"
                checked={showGuideLines}
                onChange={() => setShowGuideLines(!showGuideLines)}
              />
            }
            label="Guide Lines"
            labelPlacement="start"
          />
        </Stack>
      </Collapse>

      {/* Calibration Points Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          "&:hover": { backgroundColor: "action.hover" },
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
        }}
        onClick={() => setCalibrationPointsExpanded(!calibrationPointsExpanded)}
      >
        <Typography variant="subtitle2">Calibration Points</Typography>
        {calibrationPointsExpanded ? (
          <ExpandLessIcon fontSize="small" />
        ) : (
          <ExpandMoreIcon fontSize="small" />
        )}
      </Box>

      <Collapse in={calibrationPointsExpanded} sx={{ width: "100%" }}>
        <Stack direction="column" spacing={1} alignItems="flex-start">
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="primary"
                checked={showCalibrationPoints}
                onChange={() =>
                  setShowCalibrationPoints(!showCalibrationPoints)
                }
              />
            }
            label="Show points"
            labelPlacement="start"
            sx={{
              "& .MuiFormControlLabel-label": {
                fontSize: "0.875rem",
              },
            }}
          />

          <Button
            size="small"
            variant={isAddingCalibrationPoint ? "contained" : "outlined"}
            startIcon={<GpsFixedIcon />}
            onClick={() => {
              setIsAddingCalibrationPoint(!isAddingCalibrationPoint);
              setSelectedTool(
                isAddingCalibrationPoint ? "Select" : "CalibrationPoint"
              );
            }}
            color={isAddingCalibrationPoint ? "primary" : "inherit"}
          >
            {isAddingCalibrationPoint ? "Finished Adding" : "Add Point"}
          </Button>

          <CalibrationPointsList
            calibrationPoints={ramp.calibration_points || []}
            onEdit={(point) => setCalibrationDialog({ open: true, point })}
            onDelete={handleDeleteCalibrationPoint}
            onHighlight={setHighlightedCalibrationPoint}
          />
        </Stack>
      </Collapse>

      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          width: "100%",
          minHeight: "auto",
          "& .MuiTabs-indicator": {
            height: 2,
          },
          "& .MuiTab-root": {
            minHeight: 32,
            minWidth: 0,
            padding: "6px 8px",
            fontSize: "0.75rem",
            textTransform: "none",
            flex: 1,
          },
        }}
      >
        <Tab label="Tools" value="Tools" />
        <Tab label="Shapes" value="Shapes" />
        <Tab label="Hangars" value="Hangars" />
      </Tabs>
      {activeTab === "Tools" && (
        <Stack
          direction="column"
          alignItems="flex-start"
          spacing={1}
          width="100%"
        >
          {tools.map((tool) => {
            if (tool.name === "Spot") {
              return (
                <SplitButton
                  key={`tool-button-${tool.name}`}
                  startIcon={tool.icon}
                  variant="text"
                  color={selectedTool === tool.name ? "primary" : "inherit"}
                  size="medium"
                  defaultSelectedIndex={3}
                  options={[
                    {
                      label: "Very Small (x40ft)",
                      onClick: () => {
                        setSelectedSpotSize(40);
                        createSpotShape(40);
                      },
                    },
                    {
                      label: "Small (x60ft)",
                      onClick: () => {
                        setSelectedSpotSize(60);
                        createSpotShape(60);
                      },
                    },
                    {
                      label: "Midsize (x80ft)",
                      onClick: () => {
                        setSelectedSpotSize(80);
                        createSpotShape(80);
                      },
                    },
                    {
                      label: "S-Mid (x100ft)",
                      onClick: () => {
                        setSelectedSpotSize(100);
                        createSpotShape(100);
                      },
                    },
                    {
                      label: "Large (x120ft)",
                      onClick: () => {
                        setSelectedSpotSize(120);
                        createSpotShape(120);
                      },
                    },
                  ]}
                />
              );
            }
            return (
              <Button
                key={`tool-button-${tool.name}`}
                startIcon={tool.icon}
                variant={selectedTool === tool.name ? "contained" : "text"}
                onClick={() => {
                  setSelectedTool(tool.name);
                  tool.onClick();
                }}
              >
                {tool.name}
              </Button>
            );
          })}
          <Divider flexItem sx={{ my: 1 }} />
          <Typography variant="caption" sx={{ pl: 1, color: "text.secondary" }}>
            Bulk Operations
          </Typography>
          {bulkTools.map((tool) => (
            <Button
              key={`bulk-tool-button-${tool.name}`}
              startIcon={tool.icon}
              variant="text"
              onClick={tool.onClick}
              sx={{ justifyContent: "flex-start" }}
            >
              {tool.name}
            </Button>
          ))}
        </Stack>
      )}
      {activeTab === "Shapes" && (
        <ShapeElements
          shapes={ramp?.shapes || []}
          setShapes={setShapes}
          tools={tools}
        />
      )}
      {activeTab === "Hangars" && (
        <Stack direction="column" alignItems="flex-start" width="100%">
          {hangars.length === 0 && (
            <Typography variant="subtitle2" align="center">
              No hangars available
            </Typography>
          )}
          {hangars.map((hangar) => (
            <Button
              key={`hangar-button-${hangar.id}`}
              variant="text"
              disabled={Boolean(
                ramp.shapes.find((s) => s.hangar_id === hangar.id)
              )}
              onClick={() => {
                if (!isRampValid()) return;
                console.log(`${hangar.width} ft x ${hangar.depth} ft`);
                setShapes([
                  {
                    id: uuidv4(),
                    fbo_id: ramp.fbo_id,
                    ramp_id: ramp.id,
                    hangar_id: hangar.id,
                    tags: ["hangar"],
                    type: "Area",
                    locked: false,
                    line_style: "solid",
                    line_width: 3,
                    fill_style: "#c8cfde50",
                    stroke_style: "#3e5d8c",
                    text: hangar.name,
                    font_size: 12,
                    reference_image_height: null,
                    points: [
                      {
                        x: startingPoint.x + 0 - hangar.width / 2,
                        y: startingPoint.y + 0,
                      },
                      {
                        x: startingPoint.x + hangar.width - hangar.width / 2,
                        y: startingPoint.y + 0,
                      },
                      {
                        x: startingPoint.x + hangar.width - hangar.width / 2,
                        y: startingPoint.y + hangar.depth,
                      },
                      {
                        x: startingPoint.x + 0 - hangar.width / 2,
                        y: startingPoint.y + hangar.depth,
                      },
                    ],
                  },
                  ...ramp.shapes,
                ]);
              }}
              startIcon={<AddIcon />}
            >
              {hangar.name}
            </Button>
          ))}
        </Stack>
      )}
      <Divider flexItem />

      <CalibrationPointDialog
        open={calibrationDialog.open}
        onClose={() => setCalibrationDialog({ open: false })}
        onSave={handleSaveCalibrationPoint}
        point={calibrationDialog.point}
        x={calibrationDialog.x || 0}
        y={calibrationDialog.y || 0}
        rampId={ramp.id}
      />

      <GISImportDialog
        open={gisImportDialogOpen}
        onClose={() => setGisImportDialogOpen(false)}
        onImportShapes={handleImportGISShapes}
        ramp={ramp}
      />

      <EgressCalculationDialog
        open={egressDialogOpen}
        onClose={() => setEgressDialogOpen(false)}
        result={egressResult}
      />
    </Stack>
  );
};
